import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { UpdatePropertyCleaningCapacityRequest } from '@/shared/api';
import { Button, Card, CardBody, FormField, Input, SourceSection } from '@/shared/components';

interface Props extends UpdatePropertyCleaningCapacityRequest {
  saving: boolean;
  readOnly?: boolean;
  onSave: (value: UpdatePropertyCleaningCapacityRequest) => Promise<void>;
}

export function CleaningCapacityCard({
  availableBedCount,
  availableSofaBedCount,
  saving,
  readOnly = false,
  onSave,
}: Props) {
  const { t } = useTranslation();
  const [beds, setBeds] = useState(availableBedCount);
  const [sofas, setSofas] = useState(availableSofaBedCount);
  const [dirty, setDirty] = useState(false);
  const active = beds + sofas > 0;

  useEffect(() => {
    if (!dirty) {
      setBeds(availableBedCount);
      setSofas(availableSofaBedCount);
    }
  }, [availableBedCount, availableSofaBedCount, dirty]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void onSave({ availableBedCount: beds, availableSofaBedCount: sofas })
      .then(() => setDirty(false))
      .catch(() => undefined);
  };

  return (
    <Card>
      <CardBody className="p-5 sm:p-6">
        <SourceSection source="internal" subtitle={t('admin.cleaningCapacity.subtitle')} />
        <form onSubmit={submit} className="mt-5 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={t('admin.cleaningCapacity.beds')} required>
              <Input
                type="number"
                min={0}
                max={20}
                value={beds}
                disabled={readOnly}
                onChange={(event) => {
                  setBeds(Number(event.target.value));
                  setDirty(true);
                }}
              />
            </FormField>
            <FormField label={t('admin.cleaningCapacity.sofaBeds')} required>
              <Input
                type="number"
                min={0}
                max={20}
                value={sofas}
                disabled={readOnly}
                onChange={(event) => {
                  setSofas(Number(event.target.value));
                  setDirty(true);
                }}
              />
            </FormField>
          </div>
          <p className="text-ink-soft text-sm" role="status">
            {active ? t('admin.cleaningCapacity.active') : t('admin.cleaningCapacity.inactive')}
          </p>
          {!readOnly && (
            <Button type="submit" variant="brand" loading={saving}>
              {t('admin.cleaningCapacity.save')}
            </Button>
          )}
        </form>
      </CardBody>
    </Card>
  );
}
